import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3, PieChart as PieIcon } from 'lucide-react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    PieChart, Pie, Cell,
} from 'recharts';
import { Card, Empty } from '../ui/Card.jsx';
import {
    fetchQuestions,
    fetchDistribution,
    fetchCrossAnalysis,
    fetchDemographicFields,
} from '../../api/survey.js';

/**
 * AnalyticsPanel — 數據 tab
 *
 *   ControlsBar: 題目 / 日期 / 圖表類型
 *   DistributionSection: 單題分布 (bar | pie)
 *   CrossAnalysisSection: demographic × 該題 (grouped bar)
 */

const COLORS = ['#c84a2c', '#7b9659', '#c4892d', '#7059a4', '#5e7a91', '#c4674a', '#6e6358', '#5e7a4e', '#a05e7a'];

const DEMO_LABEL_KEYS = [
    'age', 'gender', 'education', 'occupation', 'income', 'district', 'region',
    'party', 'marital', 'religion', 'ethnicity', 'housing', 'residence',
    'household', 'voteIntent',
];

export function AnalyticsPanel({ avatarId }) {
    const { t } = useTranslation('survey');
    // ── controls state ──
    const [questions, setQuestions] = useState([]);
    const [qid, setQid] = useState(null);
    const [chartType, setChartType] = useState('bar'); // 'bar' | 'pie'
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // ── demographic fields ──
    const [demoFields, setDemoFields] = useState({ defaultFields: [], customFields: [], fieldLabels: {} });
    const [crossDemoField, setCrossDemoField] = useState(null); // 選哪個 demographic 跑交叉

    const [loadingQs, setLoadingQs] = useState(true);
    const [error, setError] = useState(null);

    // 載題庫 + demographic fields（並行）
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [qRes, dfRes] = await Promise.all([
                    fetchQuestions(avatarId),
                    fetchDemographicFields(avatarId).catch(() => ({ data: { defaultFields: [], customFields: [], fieldLabels: {} } })),
                ]);
                if (cancelled) return;
                const qs = (qRes.data || qRes.questions || []).filter((q) => q.isActive !== false)
                    .sort((a, b) => (a.order || 0) - (b.order || 0));
                setQuestions(qs);
                if (qs.length > 0) setQid(qs[0].id);

                const df = dfRes.data || { defaultFields: [], customFields: [], fieldLabels: {} };
                setDemoFields(df);
                const allDemo = [...(df.defaultFields || []), ...(df.customFields || [])];
                if (allDemo.length > 0) setCrossDemoField(allDemo[0]);
            } catch (e) {
                if (!cancelled) setError(e);
            } finally {
                if (!cancelled) setLoadingQs(false);
            }
        })();
        return () => { cancelled = true; };
    }, [avatarId]);

    const dateFilters = useMemo(() => {
        const f = {};
        if (startDate) f.startDate = startDate;
        if (endDate) f.endDate = endDate;
        return f;
    }, [startDate, endDate]);

    if (loadingQs) return <div className="text-sm text-ink-400 italic">{t('analytics.loadingQuestions')}</div>;
    if (error) return <div className="text-sm text-danger-500">{error.message}</div>;
    if (!questions.length) {
        return (
            <Card>
                <Empty icon={BarChart3} title={t('analytics.noQuestionsTitle')} description={t('analytics.noQuestionsDesc')} />
            </Card>
        );
    }

    const selectedQ = questions.find((q) => q.id === qid);
    const allDemoFields = [...(demoFields.defaultFields || []), ...(demoFields.customFields || [])];
    const labelOf = (k) => demoFields.fieldLabels?.[k]
        || (DEMO_LABEL_KEYS.includes(k) ? t(`demo.${k}`) : null)
        || k;

    return (
        <div className="space-y-5">
            {/* ── Controls ── */}
            <Card>
                <div className="space-y-3">
                    {/* row 1：題目選單獨佔一列 */}
                    <div className="flex items-center gap-2">
                        <label className="mono-caption text-ink-500 shrink-0 w-12">{t('analytics.question')}</label>
                        <select
                            value={qid || ''}
                            onChange={(e) => setQid(e.target.value)}
                            className="flex-1 min-w-0 px-3 py-2 text-sm bg-white border border-cream-200 rounded-md focus:outline-none focus:border-accent-500 truncate"
                        >
                            {questions.map((q) => (
                                <option key={q.id} value={q.id}>
                                    {(q.order ? `Q${q.order}. ` : '')}{q.question || q.id}
                                </option>
                            ))}
                        </select>
                    </div>
                    {/* row 2：日期 + 圖表類型 */}
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 pl-14">
                        <DateRange startDate={startDate} endDate={endDate} onChange={(s, e) => { setStartDate(s); setEndDate(e); }} />
                        <div className="ml-auto"><ChartTypeToggle value={chartType} onChange={setChartType} /></div>
                    </div>
                </div>
            </Card>

            {/* ── 單題分布 ── */}
            <DistributionSection
                avatarId={avatarId}
                qid={qid}
                question={selectedQ}
                chartType={chartType}
                dateFilters={dateFilters}
            />

            {/* ── 交叉分析 ── */}
            {allDemoFields.length > 0 && (
                <CrossAnalysisSection
                    avatarId={avatarId}
                    qid={qid}
                    question={selectedQ}
                    crossDemoField={crossDemoField}
                    onChangeCrossDemoField={setCrossDemoField}
                    allDemoFields={allDemoFields}
                    labelOf={labelOf}
                    dateFilters={dateFilters}
                />
            )}
        </div>
    );
}

// ───────── Controls ─────────

function DateRange({ startDate, endDate, onChange }) {
    const { t } = useTranslation('survey');
    return (
        <div className="flex items-center gap-2">
            <label className="mono-caption text-ink-500">{t('analytics.date')}</label>
            <input
                type="date"
                value={startDate}
                onChange={(e) => onChange(e.target.value, endDate)}
                className="px-2 py-1.5 text-xs bg-white border border-cream-200 rounded-md focus:outline-none focus:border-accent-500"
            />
            <span className="text-xs text-ink-400">~</span>
            <input
                type="date"
                value={endDate}
                onChange={(e) => onChange(startDate, e.target.value)}
                className="px-2 py-1.5 text-xs bg-white border border-cream-200 rounded-md focus:outline-none focus:border-accent-500"
            />
            {(startDate || endDate) && (
                <button
                    onClick={() => onChange('', '')}
                    className="text-[11px] text-ink-400 hover:text-ink-700 underline"
                >{t('analytics.clear')}</button>
            )}
        </div>
    );
}

function ChartTypeToggle({ value, onChange }) {
    return (
        <div className="flex items-center gap-1 bg-cream-100 border border-cream-200 rounded-md p-0.5">
            <ToggleBtn active={value === 'bar'} onClick={() => onChange('bar')}>
                <BarChart3 size={13} /> Bar
            </ToggleBtn>
            <ToggleBtn active={value === 'pie'} onClick={() => onChange('pie')}>
                <PieIcon size={13} /> Pie
            </ToggleBtn>
        </div>
    );
}

function ToggleBtn({ active, onClick, children }) {
    return (
        <button
            onClick={onClick}
            className={[
                'inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded transition',
                active ? 'bg-white text-ink-900 shadow-sm font-medium' : 'text-ink-500 hover:text-ink-700',
            ].join(' ')}
        >
            {children}
        </button>
    );
}

// ───────── Distribution ─────────

function DistributionSection({ avatarId, qid, question, chartType, dateFilters }) {
    const { t } = useTranslation('survey');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!qid) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        (async () => {
            try {
                const r = await fetchDistribution(avatarId, qid, dateFilters);
                if (!cancelled) setData(r.data || null);
            } catch (e) {
                if (!cancelled) setError(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [avatarId, qid, dateFilters]);

    return (
        <Card>
            <div className="flex items-start justify-between mb-4">
                <div>
                    <div className="mono-caption text-ink-400">{t('analytics.distribution')}</div>
                    <div className="text-sm font-medium text-ink-900 mt-0.5">{question?.question || qid}</div>
                </div>
                {data && (
                    <div className="text-right">
                        <div className="text-2xl font-semibold text-ink-900 tnum">{data.total ?? 0}</div>
                        <div className="mono-caption">{t('analytics.sampleSize')}</div>
                    </div>
                )}
            </div>

            {loading ? (
                <div className="text-sm text-ink-400 italic text-center py-8">{t('analytics.loadingDistribution')}</div>
            ) : error ? (
                <div className="text-sm text-danger-500">{error.message}</div>
            ) : !data || (data.total ?? 0) === 0 ? (
                <div className="text-sm text-ink-400 italic text-center py-8">{t('analytics.noAnswerData')}</div>
            ) : (
                <DistributionChart distribution={data.distribution || []} total={data.total} chartType={chartType} t={t} />
            )}
        </Card>
    );
}

function DistributionChart({ distribution, total, chartType, t }) {
    const sorted = [...distribution].sort((a, b) => (b.count || 0) - (a.count || 0));
    const chartData = sorted.map((d) => ({
        name: d.label || d.answer || '—',
        value: d.count || 0,
        percentage: total ? +((d.count / total) * 100).toFixed(1) : 0,
    }));

    if (chartType === 'pie') {
        return (
            <div className="h-[340px]">
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={chartData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={120}
                            label={({ name, percentage }) => `${name} ${percentage}%`}
                        >
                            {chartData.map((_, i) => (
                                <Cell key={i} fill={COLORS[i % COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip formatter={(v, n, p) => [`${v} (${p.payload.percentage}%)`, p.payload.name]} />
                        <Legend />
                    </PieChart>
                </ResponsiveContainer>
            </div>
        );
    }

    return (
        <div className="h-[340px]">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 60, left: 50, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee5d0" />
                    <XAxis type="number" tick={{ fontSize: 11, fill: '#7c6f60' }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: '#3a342d' }} width={120} />
                    <Tooltip formatter={(v, _, p) => [`${v} (${p.payload.percentage}%)`, t('analytics.sampleSize')]} />
                    <Bar dataKey="value" fill="#c84a2c" radius={[0, 4, 4, 0]}>
                        {chartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

// ───────── Cross Analysis ─────────

function CrossAnalysisSection({ avatarId, qid, question, crossDemoField, onChangeCrossDemoField, allDemoFields, labelOf, dateFilters }) {
    const { t } = useTranslation('survey');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!qid || !crossDemoField) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        (async () => {
            try {
                const r = await fetchCrossAnalysis(avatarId, crossDemoField, qid, dateFilters);
                if (!cancelled) setData(r.data || null);
            } catch (e) {
                if (!cancelled) setError(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [avatarId, qid, crossDemoField, dateFilters]);

    return (
        <Card>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                    <div className="mono-caption text-ink-400">{t('analytics.crossAnalysis')}</div>
                    <div className="text-sm font-medium text-ink-900 mt-0.5">
                        {labelOf(crossDemoField)} × {question?.question || qid}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <label className="mono-caption text-ink-500">{t('analytics.crossField')}</label>
                    <select
                        value={crossDemoField || ''}
                        onChange={(e) => onChangeCrossDemoField(e.target.value)}
                        className="px-2.5 py-1.5 text-xs bg-white border border-cream-200 rounded-md focus:outline-none focus:border-accent-500"
                    >
                        {allDemoFields.map((f) => (
                            <option key={f} value={f}>{labelOf(f)}</option>
                        ))}
                    </select>
                </div>
            </div>

            {loading ? (
                <div className="text-sm text-ink-400 italic text-center py-8">{t('analytics.loadingCross')}</div>
            ) : error ? (
                <div className="text-sm text-danger-500">{error.message}</div>
            ) : !data || !(data.crossAnalysis || []).length ? (
                <div className="text-sm text-ink-400 italic text-center py-8">
                    {t('analytics.noCrossData')}
                </div>
            ) : (
                <CrossAnalysisChart raw={data.crossAnalysis} />
            )}
        </Card>
    );
}

/**
 * cross-analysis raw shape:
 *   [{ demographicValue, total, distribution: [{label, count, percentage}] }]
 *
 * 轉成 recharts 用的 row：
 *   [{ name: demographicValue, total, [answerLabel]: count, ... }]
 *
 * 每個 answer label 是一個 Bar（同 X 軸下並列 = grouped bar）
 */
function CrossAnalysisChart({ raw }) {
    // 收集所有 answer labels
    const answerLabelSet = new Set();
    raw.forEach((row) => (row.distribution || []).forEach((d) => answerLabelSet.add(d.label || d.answer || '—')));
    const answerLabels = [...answerLabelSet];

    const chartData = raw.map((row) => {
        const item = { name: row.demographicValue || '—', total: row.total || 0 };
        (row.distribution || []).forEach((d) => {
            item[d.label || d.answer || '—'] = d.count || 0;
        });
        return item;
    });

    return (
        <div className="h-[400px]">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee5d0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#3a342d' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#7c6f60' }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    {answerLabels.map((label, i) => (
                        <Bar key={label} dataKey={label} fill={COLORS[i % COLORS.length]} radius={[4, 4, 0, 0]} />
                    ))}
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}
